import React, { useState } from "react";
import {
    FileSearch,
    BarChart3,
    Sparkles,
    Compass,
    Calculator,
    CalendarCheck,
    ArrowRight,
} from "lucide-react";
import CheckMyFileModal from "./CheckMyFileModal";
import { logIntent } from "@/lib/api";

/**
 * Six primary jump points for landing visitors, rendered as two very large
 * vertical rails flanking the Hero on desktop (3 left + 3 right), and a
 * stacked 2-column grid on mobile/tablet where side rails wouldn't fit.
 *
 * LEFT  (3): Check my file · Compare 1,000s of rates · Tell us a few details
 * RIGHT (3): Book an appointment · What are you hoping to achieve? · All calculators
 */
const CALENDLY_URL = "https://calendly.com/zayn514/30min";

const ACTIONS = [
    {
        key: "check-my-file",
        icon: FileSearch,
        label: "Check my file",
        sub: "Free credit report",
        action: "check-my-file-modal",
    },
    {
        key: "compare-rates",
        icon: BarChart3,
        label: "Compare 1,000s of rates",
        sub: "Large UK lender panel",
        href: "#rate-compare-section",
    },
    {
        key: "live-quote",
        icon: Sparkles,
        label: "Tell us a few details",
        sub: "See your live rates",
        href: "#mortgage-cta-section",
    },
    {
        key: "book",
        icon: CalendarCheck,
        label: "Book an appointment",
        sub: "30-minute video call",
        href: CALENDLY_URL,
        external: true,
        intent: "calendly",
    },
    {
        key: "wizard",
        icon: Compass,
        label: "What are you hoping to achieve?",
        sub: "60-second match",
        href: "#lead-wizard-section",
    },
    {
        key: "calculators",
        icon: Calculator,
        label: "All calculators",
        sub: "6 tools · Repay · Borrow · BTL",
        href: "#mortgage-calculators-section",
    },
];

const LEFT_ACTIONS = ACTIONS.slice(0, 3);
const RIGHT_ACTIONS = ACTIONS.slice(3);

/* ─────────── Card component (shared between rail + mobile stack) ─────────── */
function QuickCard({ action, side, size, onModal }) {
    const { key, icon: Icon, label, sub, href, external, action: actionType, intent } = action;

    const sizeCls = {
        lg: {
            wrap: "px-7 py-7 gap-5 border-[4px] rounded-3xl",
            circle: "h-20 w-20 rounded-2xl",
            icon: 36,
            label: "text-[22px] leading-[1.1]",
            sub: "text-base mt-1.5",
        },
        md: {
            wrap: "px-3.5 py-3 gap-3 border-[3px] rounded-2xl",
            circle: "h-11 w-11 rounded-lg shrink-0",
            icon: 20,
            label: "text-[14px] leading-[1.15]",
            sub: "text-[11px] mt-0.5",
        },
    }[size];

    const handleClick = (e) => {
        if (intent) logIntent(intent);
        if (actionType === "check-my-file-modal") {
            e.preventDefault();
            logIntent("message");
            onModal?.();
            return;
        }
        if (external) return;
        if (href && href.startsWith("#")) {
            e.preventDefault();
            document
                .getElementById(href.slice(1))
                ?.scrollIntoView({ behavior: "smooth" });
        }
    };

    const commonCls = `group flex items-center bg-brand-surface/95 backdrop-blur-md border-brand-border hover:border-brand-primary hover:bg-brand-surface hover:-translate-y-1 hover:shadow-[0_24px_60px_-15px_rgba(194,65,12,0.55)] transition-all duration-300 ${
        sizeCls.wrap
    } ${side === "right" ? "flex-row-reverse text-right" : ""}`;

    const inner = (
        <>
            <span
                className={`bg-brand-bg group-hover:bg-brand-primary flex items-center justify-center text-brand-primary group-hover:text-white shrink-0 transition-colors ${sizeCls.circle}`}
            >
                <Icon size={sizeCls.icon} strokeWidth={2.4} />
            </span>
            <span className="flex-1 min-w-0">
                <span
                    className={`block font-black text-brand-primary ${sizeCls.label}`}
                >
                    {label}
                </span>
                <span
                    className={`block text-brand-muted font-bold leading-tight ${sizeCls.sub}`}
                >
                    {sub}
                </span>
            </span>
        </>
    );

    if (actionType === "check-my-file-modal") {
        return (
            <button
                type="button"
                onClick={handleClick}
                data-testid={`quick-${key}`}
                className={`${commonCls} text-left w-full`}
            >
                {inner}
            </button>
        );
    }

    return (
        <a
            href={href}
            onClick={handleClick}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
            data-testid={`quick-${key}`}
            className={commonCls}
        >
            {inner}
        </a>
    );
}

/* ─────────── Desktop side rails (xl+) ─────────── */
export function QuickActionsRails() {
    const [cmfOpen, setCmfOpen] = useState(false);
    return (
        <>
            <div
                aria-hidden="false"
                data-testid="quick-actions-rails"
                className="hidden xl:block pointer-events-none absolute inset-0 z-20"
            >
                <div
                    data-testid="quick-actions-rail-left"
                    className="absolute left-4 2xl:left-8 top-24 xl:top-28 flex flex-col gap-4 2xl:gap-5 pointer-events-auto w-[260px] 2xl:w-[340px]"
                >
                    {LEFT_ACTIONS.map((a) => (
                        <QuickCard
                            key={a.key}
                            action={a}
                            side="left"
                            size="lg"
                            onModal={() => setCmfOpen(true)}
                        />
                    ))}
                </div>
                <div
                    data-testid="quick-actions-rail-right"
                    className="absolute right-4 2xl:right-8 top-24 xl:top-28 flex flex-col gap-4 2xl:gap-5 pointer-events-auto w-[260px] 2xl:w-[340px]"
                >
                    {RIGHT_ACTIONS.map((a) => (
                        <QuickCard
                            key={a.key}
                            action={a}
                            side="right"
                            size="lg"
                            onModal={() => setCmfOpen(true)}
                        />
                    ))}
                </div>
            </div>
            <CheckMyFileModal open={cmfOpen} onClose={() => setCmfOpen(false)} />
        </>
    );
}

/* ─────────── Mobile / tablet / laptop stacked grid (below xl) ─────────── */
export function QuickActionsStack() {
    const [cmfOpen, setCmfOpen] = useState(false);
    return (
        <>
            <section
                data-testid="quick-actions-stack"
                className="xl:hidden relative bg-brand-surfaceAlt border-b-2 border-brand-border"
            >
                <div className="mx-auto max-w-7xl px-3 md:px-6 py-3 md:py-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-2 md:gap-3">
                    {ACTIONS.map((a) => (
                        <QuickCard
                            key={a.key}
                            action={a}
                            side="left"
                            size="md"
                            onModal={() => setCmfOpen(true)}
                        />
                    ))}
                </div>
            </section>
            <CheckMyFileModal open={cmfOpen} onClose={() => setCmfOpen(false)} />
        </>
    );
}

export default function QuickActions() {
    return <QuickActionsStack />;
}

/* Back-compat named export (kept so any old import keeps working) */
export { QuickActionsStack as QuickActionsHorizontal };
